import _ from 'lodash';

import { formatPhoneNumberForView, normalizeRequestPhoneNumber } from 'utils/data';

import { POA_DESIGNATION_OPTIONS, STATE_TO_POA_DESIGNATION } from './POA.constants';

export const getDefaultPoaDesignationByState = stateAbbreviation => {
  if (_.isNil(stateAbbreviation) || stateAbbreviation === '') {
    return null;
  }

  return STATE_TO_POA_DESIGNATION[stateAbbreviation] ?? null;
};

export const getPoaDesignationOption = designationValue => {
  if (_.isNil(designationValue) || designationValue === '') {
    return null;
  }

  return POA_DESIGNATION_OPTIONS.find(option => option.value === designationValue) ?? null;
};

export const getPoaDesignationValue = designation => {
  if (_.isNil(designation) || designation === '') {
    return null;
  }

  if (_.isString(designation)) {
    return designation;
  }

  return designation?.value ?? null;
};

export const getPoaCapacityLabel = designation => {
  const value = getPoaDesignationValue(designation);

  return getPoaDesignationOption(value)?.name ?? null;
};

export const getPoaVerbiage = ({ borrowerFullName, poaName, poaCapacityLabel }) => {
  if (!borrowerFullName || !poaName || !poaCapacityLabel) {
    return '';
  }

  return `${borrowerFullName} By: ${poaName}, ${poaCapacityLabel} for ${borrowerFullName}`;
};

export const getNormalizedSuffix = suffix => {
  if (_.isNil(suffix)) {
    return null;
  }

  if (_.isPlainObject(suffix)) {
    return suffix.value ?? null;
  }

  return suffix;
};

const escapeRegExpLiteral = literal => literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const replaceTokenInVerbiage = ({ verbiage, previousToken, nextToken }) => {
  if (!verbiage || !previousToken || !nextToken || previousToken === nextToken) {
    return verbiage;
  }

  const pattern = new RegExp(`(?<!\\w)${escapeRegExpLiteral(previousToken)}(?!\\w)`, 'g');

  return verbiage.replace(pattern, nextToken);
};

export const replacePoaNameInVerbiage = ({ verbiage, previousPoaName, nextPoaName }) =>
  replaceTokenInVerbiage({
    verbiage,
    previousToken: previousPoaName,
    nextToken: nextPoaName,
  });

export const replacePoaCapacityInVerbiage = ({
  verbiage,
  previousCapacityLabel,
  nextCapacityLabel,
}) =>
  replaceTokenInVerbiage({
    verbiage,
    previousToken: previousCapacityLabel,
    nextToken: nextCapacityLabel,
  });

export const formatPoaForForm = powerOfAttorney => ({
  ...powerOfAttorney,
  phone: formatPhoneNumberForView(powerOfAttorney?.phone),
});

export const formatPoaForRequest = powerOfAttorney => ({
  name: powerOfAttorney?.name || null,
  verbiage: powerOfAttorney?.verbiage || null,
  designation: getPoaDesignationValue(powerOfAttorney?.designation),
  // The API keeps the stored contact when it receives null and clears it on an empty string, so a
  // contact the form never loaded must go as null rather than wipe the stored value.
  email: _.isNil(powerOfAttorney?.email) ? null : powerOfAttorney.email.trim(),
  phone: _.isNil(powerOfAttorney?.phone)
    ? null
    : normalizeRequestPhoneNumber(powerOfAttorney.phone),
});
